import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { io, type Socket } from 'socket.io-client';
import Session from 'supertokens-web-js/recipe/session';

import { REALTIME_NAMESPACE } from '@/shared/realtime-events';

@Injectable({ providedIn: 'root' })
export class RealtimeSocketService {
  private socket: Socket | null = null;
  private connectPromise: Promise<Socket> | null = null;
  private reconnectAttemptInProgress = false;

  private readonly zone = inject(NgZone);

  listen<TPayload>(eventName: string): Observable<TPayload> {
    return new Observable<TPayload>((subscriber) => {
      let disposed = false;
      let socketHandler: ((payload: TPayload) => void) | null = null;

      void this.ensureSocket()
        .then((socket) => {
          if (disposed) {
            return;
          }

          socketHandler = (payload: TPayload) => {
            this.zone.run(() => subscriber.next(payload));
          };
          socket.on(eventName, socketHandler);
        })
        .catch((error) => {
          this.zone.run(() => subscriber.error(error));
        });

      return () => {
        disposed = true;
        if (this.socket && socketHandler) {
          this.socket.off(eventName, socketHandler);
        }
      };
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connectPromise = null;
  }

  private async ensureSocket(): Promise<Socket> {
    if (this.socket) {
      if (!this.socket.connected && !this.socket.active) {
        await this.connectSocket(this.socket);
      }
      return this.socket;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = this.createAndConnectSocket().finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  private async createAndConnectSocket(): Promise<Socket> {
    const socket = io(this.getRealtimeUrl(), {
      autoConnect: false,
      transports: ['websocket'],
      withCredentials: true
    });

    socket.on('connect_error', (error) => {
      void this.handleConnectError(socket, error);
    });

    await this.connectSocket(socket);
    this.socket = socket;
    return socket;
  }

  private async connectSocket(socket: Socket): Promise<void> {
    const accessToken = await Session.getAccessToken().catch(() => undefined);
    socket.auth = accessToken ? { accessToken } : {};

    if (socket.connected) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const handleConnect = () => {
        cleanup();
        resolve();
      };
      const handleError = (error: Error) => {
        cleanup();
        reject(error);
      };
      const cleanup = () => {
        socket.off('connect', handleConnect);
        socket.off('connect_error', handleError);
      };

      socket.on('connect', handleConnect);
      socket.on('connect_error', handleError);
      socket.connect();
    });
  }

  private async handleConnectError(socket: Socket, error: Error): Promise<void> {
    if (!this.isUnauthorizedError(error) || this.reconnectAttemptInProgress || !this.socket) {
      return;
    }

    this.reconnectAttemptInProgress = true;

    try {
      const refreshed = await Session.attemptRefreshingSession().catch(() => false);
      if (!refreshed) {
        return;
      }

      const accessToken = await Session.getAccessToken();
      socket.auth = accessToken ? { accessToken } : {};
      if (!socket.connected) {
        socket.connect();
      }
    } finally {
      this.reconnectAttemptInProgress = false;
    }
  }

  private isUnauthorizedError(error: Error): boolean {
    return error.message.toLowerCase().includes('authoriz') || error.message.toLowerCase().includes('token');
  }

  private getRealtimeUrl(): string {
    return `${window.location.origin}/${REALTIME_NAMESPACE}`;
  }
}
